<template>
  <div class="membership-page">
    <header class="membership-hero">
      <span class="membership-hero__eyebrow">Tu cuenta</span>
      <h1 class="membership-hero__title">
        Membresías
      </h1>
      <p class="membership-hero__subtitle">
        Elige hasta dónde quieres llegar con YoConstruyo.
      </p>

      <div
        v-if="membership"
        class="membership-status"
      >
        <span :class="['membership-status__plan', { 'membership-status__plan--paid': isPaidPlan }]">
          {{ membership.plan_name }}
        </span>
        <span class="membership-status__detail">
          <template v-if="membership.expires_at">
            Vigente hasta {{ formatDate(membership.expires_at) }}
            <template v-if="daysLeft !== null">
              · {{ daysLeftLabel }}
            </template>
          </template>
          <template v-else>
            Sin fecha de corte
          </template>
        </span>
      </div>
    </header>

    <b-alert
      v-if="upgradeMessage"
      variant="warning"
      show
      class="membership-upgrade-alert"
    >
      <div class="alert-body d-flex align-items-center">
        <feather-icon
          icon="LockIcon"
          class="mr-1"
        />
        <span>{{ upgradeMessage }}</span>
      </div>
    </b-alert>

    <div
      v-if="showPurchaseGuide"
      class="membership-guide"
    >
      <feather-icon
        icon="ZapIcon"
        size="20"
        class="membership-guide__icon"
      />
      <div>
        <strong>Estás a tres pasos de PRO</strong>
        <div class="membership-guide__detail">
          Elige cuántos meses quieres pagar, transfiere el total y sube tu comprobante.
          Mientras lo revisamos tu cuenta funciona con el plan FREE.
        </div>
      </div>
    </div>

    <div
      v-if="pendingRequest"
      class="membership-pending"
    >
      <feather-icon
        icon="ClockIcon"
        size="20"
        class="membership-pending__icon"
      />
      <div>
        <strong>Tu comprobante está en revisión</strong>
        <div class="membership-pending__detail">
          Enviaste {{ formatMoney(pendingRequest.amount) }} por {{ pendingRequest.months }}
          {{ pendingRequest.months === 1 ? 'mes' : 'meses' }} el {{ formatDate(pendingRequest.created_at) }}.
          Te avisamos en cuanto lo revisemos.
        </div>
      </div>
    </div>

    <b-overlay :show="loading">
      <div class="plan-grid">
        <article
          v-for="plan in visiblePlans"
          :key="plan.id"
          :class="['plan-card', {
            'plan-card--current': isCurrentPlan(plan),
            'plan-card--paid': plan.purchasable,
          }]"
        >
          <div
            v-if="isCurrentPlan(plan)"
            class="plan-card__ribbon"
          >
            <feather-icon
              icon="CheckIcon"
              size="13"
            />
            Tu plan actual
          </div>

          <header class="plan-card__head">
            <div class="plan-card__icon">
              <feather-icon
                :icon="plan.purchasable ? 'ZapIcon' : 'StarIcon'"
                size="22"
              />
            </div>
            <h2 class="plan-card__name">
              {{ plan.name }}
            </h2>
            <div class="plan-card__price">
              <template v-if="Number(plan.price) > 0">
                {{ formatMoney(plan.price) }}<span class="plan-card__period">/mes</span>
              </template>
              <template v-else>
                Gratis
              </template>
            </div>
            <p
              v-if="plan.tagline"
              class="plan-card__tagline"
            >
              {{ plan.tagline }}
            </p>
          </header>

          <ul class="plan-features">
            <li
              v-for="bullet in planBullets(plan)"
              :key="`${plan.id}-${bullet.key}`"
              :class="['plan-features__item', { 'plan-features__item--off': !bullet.ok }]"
            >
              <feather-icon
                :icon="bullet.ok ? 'CheckCircleIcon' : 'XCircleIcon'"
                size="16"
                class="plan-features__icon"
              />
              <span>{{ bullet.text }}</span>
            </li>
          </ul>

          <footer class="plan-card__footer">
            <template v-if="plan.purchasable && !pendingRequest">
              <div
                ref="purchase"
                class="plan-card__cta-title"
              >
                {{ isCurrentPlan(plan) ? 'Agrega meses a tu membresía' : 'Contrata tu plan' }}
              </div>

              <div class="month-picker">
                <button
                  v-for="option in termsFor(plan)"
                  :key="option.months"
                  type="button"
                  :class="['month-picker__option', { 'month-picker__option--active': months === option.months }]"
                  @click="months = option.months"
                >
                  <span
                    v-if="option.discount_percent"
                    class="month-picker__badge"
                  >-{{ option.discount_percent }}%</span>
                  <span class="month-picker__number">{{ option.months }}</span>
                  <span class="month-picker__label">{{ option.months === 1 ? 'mes' : 'meses' }}</span>
                </button>
              </div>

              <div
                v-if="!quoteFor(plan).percent && maxDiscount(plan)"
                class="plan-total__hint plan-total__hint--offer"
              >
                <feather-icon
                  icon="TagIcon"
                  size="13"
                  class="mr-25"
                />
                Paga {{ maxDiscount(plan).months }} meses por adelantado y ahorra {{ maxDiscount(plan).discount_percent }}%
              </div>

              <div class="plan-total">
                <template v-if="quoteFor(plan).percent">
                  <div class="plan-total__row">
                    <span class="plan-total__label">Precio regular ({{ quoteFor(plan).months }} meses)</span>
                    <s class="plan-total__list">{{ formatMoney(quoteFor(plan).list) }}</s>
                  </div>
                  <div class="plan-total__row plan-total__row--discount">
                    <span>Descuento {{ quoteFor(plan).percent }}%</span>
                    <span>-{{ formatMoney(quoteFor(plan).discount) }}</span>
                  </div>
                </template>
                <div :class="['plan-total__row', { 'plan-total__row--total': quoteFor(plan).percent }]">
                  <span class="plan-total__label">Total a pagar</span>
                  <span class="plan-total__amount">{{ formatMoney(quoteFor(plan).total) }}</span>
                </div>
                <div
                  v-if="quoteFor(plan).percent"
                  class="plan-total__monthly"
                >
                  Equivale a {{ formatMoney(quoteFor(plan).monthly) }} al mes
                </div>
              </div>

              <div
                v-if="newExpiryLabel"
                class="plan-total__hint"
              >
                <feather-icon
                  icon="CalendarIcon"
                  size="13"
                  class="mr-25"
                />
                {{ newExpiryLabel }}
              </div>

              <div class="plan-transfer">
                <div class="plan-transfer__title">
                  Transfiere el total a esta cuenta y sube tu comprobante
                </div>
                <bank-transfer-card :payment="bankAccount" />
              </div>

              <label
                :class="['receipt-drop', { 'receipt-drop--filled': file }]"
                @dragover.prevent
                @drop.prevent="onDrop"
              >
                <input
                  type="file"
                  class="receipt-drop__input"
                  accept="image/jpeg,image/png,application/pdf"
                  @change="onFileChange"
                >
                <feather-icon
                  :icon="file ? 'FileTextIcon' : 'UploadCloudIcon'"
                  size="20"
                  class="receipt-drop__icon"
                />
                <span class="receipt-drop__text">
                  {{ file ? file.name : 'Sube la imagen de tu pago' }}
                </span>
                <span class="receipt-drop__hint">JPG, PNG o PDF</span>
              </label>

              <b-button
                variant="primary"
                block
                class="plan-card__button"
                :disabled="!file || uploading"
                @click="uploadReceipt(plan)"
              >
                <feather-icon
                  :icon="uploading ? 'LoaderIcon' : 'SendIcon'"
                  size="15"
                  class="mr-50"
                />
                {{ uploading ? 'Enviando...' : 'Enviar comprobante' }}
              </b-button>
            </template>

            <div
              v-else-if="plan.purchasable"
              class="plan-card__note"
            >
              Ya tienes un comprobante en revisión. En cuanto lo aprobemos podrás pagar más meses.
            </div>

            <div
              v-else-if="isCurrentPlan(plan)"
              class="plan-card__note"
            >
              Es el plan con el que arranca toda cuenta. No vence nunca.
            </div>

            <div
              v-else
              class="plan-card__note"
            >
              Tu plan base. Al terminar tu membresía de paga, tu cuenta regresa aquí.
            </div>
          </footer>
        </article>
      </div>
    </b-overlay>

    <section
      v-if="history.length"
      class="membership-history"
    >
      <h2 class="membership-history__title">
        Tus pagos de membresía
      </h2>

      <div class="membership-history__list">
        <article
          v-for="request in history"
          :key="request.id"
          class="history-row"
        >
          <div class="history-row__main">
            <strong>{{ request.membership_plan_name }} · {{ request.months }}
              {{ request.months === 1 ? 'mes' : 'meses' }}</strong>
            <span
              v-if="request.discount_percent"
              class="history-row__date"
            >
              {{ request.discount_percent }}% de descuento sobre {{ formatMoney(request.list_amount) }}
            </span>
            <span class="history-row__date">
              Enviado el {{ formatDate(request.created_at) }}
              <template v-if="request.reviewed_at">
                · revisado el {{ formatDate(request.reviewed_at) }}
              </template>
            </span>
            <span
              v-if="request.status === 'rejected' && request.reject_reason"
              class="history-row__reason"
            >
              Motivo: {{ request.reject_reason }}
            </span>
          </div>

          <div class="history-row__side">
            <span class="history-row__amount">{{ formatMoney(request.amount) }}</span>
            <b-badge :variant="statusVariant(request.status)">
              {{ statusLabel(request.status) }}
            </b-badge>
            <b-link
              v-if="request.receipt_url"
              :href="request.receipt_url"
              target="_blank"
              class="history-row__receipt"
            >
              Ver comprobante
            </b-link>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
import {
  BButton, BAlert, BOverlay, BBadge, BLink,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import BankTransferCard from '@/components/invoice/BankTransferCard.vue'
import { COMPANY_BANK_ACCOUNT } from '@/utils/company'

const ALLOWED_RECEIPT_TYPES = ['image/jpeg', 'image/png', 'application/pdf']
const DEFAULT_TERMS = [1, 3, 6, 12].map(months => ({ months, discount_percent: 0 }))
const roundCents = value => Math.round(value * 100) / 100

export default {
  components: {
    BButton,
    BAlert,
    BOverlay,
    BBadge,
    BLink,
    BankTransferCard,
  },

  data() {
    return {
      bankAccount: COMPANY_BANK_ACCOUNT,
      file: null,
      months: 1,
      uploading: false,
      pendingRequest: null,
      history: [],
    }
  },

  computed: {
    ...mapGetters('memberships', {
      plans: 'membershipPlans',
      catalog: 'membershipCatalog',
      loading: 'membershipsLoading',
      // El payload del login se queda con el plan viejo en cuanto el admin lo mueve, asi
      // que la pantalla lee lo que contesto /v1/membership.
      membership: 'currentMembership',
    }),

    visiblePlans() {
      return this.plans.filter(plan => plan.active)
    },

    showPurchaseGuide() {
      return this.$route.query.plan === 'pro' && !this.pendingRequest && !this.isPaidPlan
    },

    isPaidPlan() {
      return Boolean(this.membership) && this.membership.plan_key !== 'free'
    },

    daysLeft() {
      if (!this.membership?.expires_at) return null

      const diff = new Date(this.membership.expires_at).getTime() - Date.now()

      return Math.max(Math.ceil(diff / 86400000), 0)
    },

    daysLeftLabel() {
      if (this.daysLeft === 0) return 'vence hoy'
      if (this.daysLeft === 1) return 'falta 1 día'

      return `faltan ${this.daysLeft} días`
    },

    features() {
      return this.catalog.reduce((acc, category) => acc.concat(category.features), [])
    },

    // El router manda la llave que le faltaba al plan para que la pantalla diga por que
    // acabo aqui en lugar de en la que iba.
    upgradeMessage() {
      const featureKey = this.$route.query.upgrade
      if (!featureKey) return null

      const feature = this.features.find(item => item.key === featureKey)
      const label = feature ? feature.name : 'La sección que buscabas'
      const paid = this.plans.find(plan => plan.purchasable)

      return `${label} es parte del plan ${paid ? paid.name : 'de paga'}. Contrátalo aquí para desbloquearlo.`
    },

    // Renovar no reinicia la vigencia, la estira: conviene que el cliente lo vea antes de
    // pagar y no despues.
    newExpiryLabel() {
      const base = this.isPaidPlan && this.membership?.expires_at
        ? new Date(this.membership.expires_at)
        : new Date()

      if (Number.isNaN(base.getTime())) return null

      const target = new Date(base.getTime())
      target.setMonth(target.getMonth() + Number(this.months || 1))

      return this.isPaidPlan
        ? `Tu membresía quedaría vigente hasta el ${this.formatDate(target)}`
        : `Quedaría vigente hasta el ${this.formatDate(target)}`
    },
  },

  created() {
    const plansLoaded = this.getMembershipPlans()
    this.getMyMembership()
    const requestsLoaded = this.loadRequests()

    if (this.$route.query.plan === 'pro') {
      Promise.all([plansLoaded, requestsLoaded])
        .then(() => this.$nextTick())
        .then(() => {
          const target = this.$refs.purchase?.[0]
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
        .catch(() => {})
    }
  },

  methods: {
    ...mapActions('memberships', [
      'getMembershipPlans',
      'getMyMembership',
      'getMembershipRequests',
      'createMembershipRequest',
    ]),

    isCurrentPlan(plan) {
      return this.membership?.plan_key === plan.key
    },

    termsFor(plan) {
      return plan.term_options?.length ? plan.term_options : DEFAULT_TERMS
    },

    maxDiscount(plan) {
      return this.termsFor(plan).reduce((best, option) => (
        Number(option.discount_percent) > Number(best?.discount_percent || 0) ? option : best
      ), null)
    },

    quoteFor(plan) {
      const months = Number(this.months || 1)
      const term = this.termsFor(plan).find(option => option.months === months)
      const percent = Number(term?.discount_percent || 0)
      const list = roundCents(Number(plan.price || 0) * months)
      const discount = roundCents((list * percent) / 100)
      const total = roundCents(list - discount)

      return {
        months, percent, list, discount, total, monthly: roundCents(total / months),
      }
    },

    formatMoney(value) {
      const amount = Number(value || 0)
      const digits = Number.isInteger(amount) ? 0 : 2

      return `$${amount.toLocaleString('es-MX', { minimumFractionDigits: digits, maximumFractionDigits: 2 })}`
    },

    formatDate(value) {
      if (!value) return ''

      return new Date(value).toLocaleDateString('es-MX')
    },

    // Las viñetas salen de lo que el admin dejó configurado, no de una lista fija.
    planBullets(plan) {
      return this.features.map(feature => {
        const value = plan.features[feature.key]

        if (feature.kind === 'boolean') {
          return {
            key: feature.key,
            ok: value === true,
            text: value === true ? feature.name : `Sin ${feature.name.toLowerCase()}`,
          }
        }

        if (value === null || value === undefined || value === '') {
          return { key: feature.key, ok: true, text: `${feature.name}: sin límite` }
        }

        return {
          key: feature.key,
          ok: Number(value) > 0,
          text: `${feature.name}: ${value}`,
        }
      })
    },

    // Una sola llamada trae todo el historial; el pendiente es el primero que este en
    // revision, asi no hay que pedir la misma lista dos veces.
    loadRequests() {
      return this.getMembershipRequests({}).then(response => {
        this.history = response.data || []
        this.pendingRequest = this.history.find(request => request.status === 'pending') || null
      })
    },

    statusLabel(status) {
      return {
        pending: 'En revisión',
        approved: 'Aprobado',
        rejected: 'Rechazado',
      }[status] || status
    },

    statusVariant(status) {
      return {
        pending: 'light-warning',
        approved: 'light-success',
        rejected: 'light-danger',
      }[status] || 'light-secondary'
    },

    onFileChange(event) {
      const input = event.target
      this.setFile(input.files?.[0] || null)
      // El archivo ya quedo en this.file. Se limpia el input para que volver a elegir el
      // mismo archivo despues de un error si dispare el change.
      input.value = ''
    },

    onDrop(event) {
      this.setFile(event.dataTransfer?.files?.[0] || null)
    },

    setFile(file) {
      if (!file) return

      if (!ALLOWED_RECEIPT_TYPES.includes(file.type)) {
        this.toast('Solo se permiten archivos JPG, PNG o PDF', 'warning')
        this.file = null
        return
      }

      this.file = file
    },

    async uploadReceipt(plan) {
      if (!this.file) return

      try {
        this.uploading = true
        const base64 = await this.$getBase64(this.file)

        await this.createMembershipRequest({
          membership_plan_id: plan.id,
          months: Number(this.months || 1),
          receipt_attachment: { data: base64 },
        })

        this.file = null
        await this.loadRequests()
        this.toast('Comprobante enviado. Te avisamos en cuanto lo revisemos.')
      } catch (error) {
        this.toast(error?.response?.data?.messages?.[0] || 'No se pudo enviar el comprobante', 'danger')
      } finally {
        this.uploading = false
      }
    },

    toast(title, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: 'AwardIcon', variant },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
$brand: #340668;
$ink: #25233a;
$muted: #6e6b7b;
$line: #ebe9f1;

.membership-page {
  padding-bottom: 2rem;
}

.membership-hero {
  text-align: center;
  padding: 1.5rem 0 2rem;
}

.membership-hero__eyebrow {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: $muted;
}

.membership-hero__title {
  font-size: 2.25rem;
  font-weight: 800;
  color: $ink;
  margin: 0.25rem 0 0.35rem;
}

.membership-hero__subtitle {
  color: $muted;
  margin-bottom: 1rem;
}

.membership-status {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.4rem 0.4rem 0.4rem 0.5rem;
  border: 1px solid $line;
  border-radius: 999px;
  background: #ffffff;
}

.membership-status__plan {
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  background: #f1f0f5;
  color: $muted;
}

.membership-status__plan--paid {
  background: linear-gradient(135deg, $brand, #6b34c4);
  color: #ffffff;
}

.membership-status__detail {
  font-size: 0.78rem;
  color: $muted;
  padding-right: 0.5rem;
}

.membership-upgrade-alert,
.membership-guide,
.membership-pending {
  max-width: 62rem;
  margin: 0 auto 1.25rem;
}

.membership-pending {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem 1.15rem;
  border: 1px solid #ffe3a3;
  border-radius: 0.75rem;
  background: #fffaf0;
  color: #7a5a12;
}

.membership-guide {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem 1.15rem;
  border: 1px solid rgba(52, 6, 104, 0.2);
  border-radius: 0.75rem;
  background: rgba(52, 6, 104, 0.05);
  color: $brand;
}

.membership-guide__icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.membership-guide__detail {
  font-size: 0.82rem;
  color: $ink;
  opacity: 0.85;
}

.membership-pending__icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.membership-pending__detail {
  font-size: 0.82rem;
  opacity: 0.85;
}

.plan-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
  gap: 1.5rem;
  max-width: 62rem;
  margin: 0 auto;
  align-items: start;
}

.plan-card {
  position: relative;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid $line;
  border-radius: 1.25rem;
  padding: 2rem 1.75rem 1.75rem;
  box-shadow: 0 4px 24px rgba(34, 41, 47, 0.06);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.plan-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(34, 41, 47, 0.1);
}

.plan-card--paid {
  border-color: rgba(52, 6, 104, 0.25);
}

.plan-card--current {
  border-color: $brand;
  box-shadow: 0 0 0 3px rgba(52, 6, 104, 0.12), 0 12px 32px rgba(34, 41, 47, 0.1);
}

.plan-card__ribbon {
  position: absolute;
  top: -0.75rem;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.85rem;
  border-radius: 999px;
  background: linear-gradient(135deg, $brand, #6b34c4);
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.plan-card__head {
  text-align: center;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid $line;
}

.plan-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 1rem;
  margin-bottom: 0.65rem;
  background: #f5f3fa;
  color: $brand;
}

.plan-card--paid .plan-card__icon {
  background: linear-gradient(135deg, $brand, #6b34c4);
  color: #ffffff;
}

.plan-card__name {
  font-size: 1.5rem;
  font-weight: 800;
  color: $ink;
  margin-bottom: 0.15rem;
}

.plan-card__price {
  font-size: 1.75rem;
  font-weight: 800;
  color: $brand;
  line-height: 1.1;
}

.plan-card__period {
  font-size: 0.85rem;
  font-weight: 600;
  color: $muted;
}

.plan-card__tagline {
  font-size: 0.8rem;
  color: $muted;
  margin: 0.35rem 0 0;
}

.plan-features {
  list-style: none;
  padding: 1.25rem 0 0;
  margin: 0 0 1.25rem;
  flex-grow: 1;
}

.plan-features__item {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.3rem 0;
  font-size: 0.87rem;
  color: $ink;
}

.plan-features__icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: #28c76f;
}

.plan-features__item--off {
  color: $muted;

  .plan-features__icon {
    color: #d8d6de;
  }
}

.plan-card__footer {
  border-top: 1px solid $line;
  padding-top: 1.25rem;
}

.plan-card__cta-title {
  font-size: 0.8rem;
  font-weight: 700;
  color: $ink;
  margin-bottom: 0.65rem;
}

.month-picker {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  margin: 1.1rem 0 0.9rem;
}

.month-picker__option {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.5rem 0.25rem;
  border: 1px solid $line;
  border-radius: 0.65rem;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.15s ease;
}

.month-picker__option:hover {
  border-color: rgba(52, 6, 104, 0.4);
}

.month-picker__option--active {
  border-color: $brand;
  background: rgba(52, 6, 104, 0.06);
}

.month-picker__badge {
  position: absolute;
  top: -0.6rem;
  left: 50%;
  transform: translateX(-50%);
  padding: 0.05rem 0.4rem;
  border-radius: 999px;
  background: #28c76f;
  color: #ffffff;
  font-size: 0.64rem;
  font-weight: 800;
  line-height: 1.4;
  white-space: nowrap;
}

.month-picker__number {
  font-size: 1.05rem;
  font-weight: 800;
  color: $ink;
  line-height: 1.1;
}

.month-picker__label {
  font-size: 0.68rem;
  color: $muted;
}

.plan-total {
  padding: 0.6rem 0.85rem;
  border-radius: 0.65rem;
  background: #f8f8fb;
}

.plan-total__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  font-variant-numeric: tabular-nums;

  & + & {
    margin-top: 0.3rem;
  }
}

.plan-total__list {
  font-size: 0.85rem;
  color: $muted;
}

.plan-total__row--discount {
  font-size: 0.82rem;
  font-weight: 700;
  color: #28c76f;
}

.plan-total__row--total {
  padding-top: 0.4rem;
  border-top: 1px dashed $line;
}

.plan-total__monthly {
  margin-top: 0.15rem;
  font-size: 0.72rem;
  color: $muted;
  text-align: right;
}

.plan-total__label {
  font-size: 0.78rem;
  color: $muted;
}

.plan-total__amount {
  font-size: 1.2rem;
  font-weight: 800;
  color: $brand;
}

.plan-total__hint {
  display: flex;
  align-items: center;
  font-size: 0.74rem;
  color: $muted;
  margin: 0.45rem 0 0;
}

.plan-total__hint--offer {
  margin: 0 0 0.6rem;
  font-weight: 600;
  color: #28c76f;
}

.plan-transfer {
  margin-top: 0.9rem;
}

.plan-transfer__title {
  margin-bottom: 0.45rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: $muted;
}

.receipt-drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  width: 100%;
  margin: 0.9rem 0;
  padding: 1.1rem 0.75rem;
  border: 1.5px dashed #cfc9dd;
  border-radius: 0.75rem;
  background: #fbfaff;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.receipt-drop:hover {
  border-color: $brand;
  background: rgba(52, 6, 104, 0.04);
}

.receipt-drop--filled {
  border-style: solid;
  border-color: #28c76f;
  background: rgba(40, 199, 111, 0.06);
}

.receipt-drop__input {
  display: none;
}

.receipt-drop__icon {
  color: $brand;
}

.receipt-drop--filled .receipt-drop__icon {
  color: #28c76f;
}

.receipt-drop__text {
  font-size: 0.84rem;
  font-weight: 600;
  color: $ink;
  word-break: break-all;
}

.receipt-drop__hint {
  font-size: 0.7rem;
  color: $muted;
}

.membership-history {
  max-width: 62rem;
  margin: 2rem auto 0;
}

.membership-history__title {
  font-size: 1.05rem;
  font-weight: 700;
  color: $ink;
  margin-bottom: 0.75rem;
}

.membership-history__list {
  border: 1px solid $line;
  border-radius: 0.9rem;
  background: #ffffff;
  overflow: hidden;
}

.history-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1.15rem;
  flex-wrap: wrap;

  & + & {
    border-top: 1px solid $line;
  }
}

.history-row__main {
  display: flex;
  flex-direction: column;
  min-width: 0;

  strong {
    color: $ink;
    font-size: 0.92rem;
  }
}

.history-row__date,
.history-row__reason {
  font-size: 0.76rem;
  color: $muted;
}

.history-row__side {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.history-row__amount {
  font-weight: 700;
  color: $ink;
  font-variant-numeric: tabular-nums;
}

.history-row__receipt {
  font-size: 0.78rem;
  font-weight: 600;
}

.plan-card__note {
  font-size: 0.8rem;
  color: $muted;
  text-align: center;
}
</style>
